'use client'

import { useContext, useRef, useState } from 'react'
import getConfig from 'next/config'

const {
  publicRuntimeConfig: { API_BASE_PATH },
} = getConfig()
import type { Document } from '@contentful/rich-text-types'
import {
  Box,
  Button,
  Card,
  Icon,
  Spacer,
  StackView,
  TextButton,
  TextInput,
  Tooltip,
  Typography,
} from '@telus-uds/components-web'
import { ArrowLeft, StatusError, StatusSuccess, StatusWarning } from '@telus-uds/palette-allium/build/web/icons'
import { VIEWPORT } from '@/constants/VIEWPORT'
import RichText from '@/siteBuilder/blocks/core/RichText/RichText'
import { SiteBuilderContext } from '@/siteBuilder/renderer/context/SiteBuilderContext'
import alloyEvents from '@/siteBuilder/utils/alloyAnalytics/imeiChecker'
import IMEI_CHECKER_I18N from './ImeiChecker.i18n'

type DeviceResult = {
  manufacturerName?: string
  modelName?: string
  telusStatusCode?: string
  oemCertified?: string
  telusVoLTERoaming?: string
  koodoRoaming?: string
  koodoStatusCode?: string
  publicMobileStatusCode?: string
  publicMobileRoaming?: string
}

type ApiError = {
  message: string
  normalizedImei?: string
  tacId?: string
}

type ResultCard = 'compatible' | 'splitCompatible' | 'notCompatible'

const getResultCard = (result: DeviceResult): ResultCard => {
  if (result.telusStatusCode === 'Yes' && result.telusVoLTERoaming === 'Yes') return 'compatible'
  if (result.telusStatusCode === 'Yes' && result.telusVoLTERoaming !== 'Yes') return 'splitCompatible'
  return 'notCompatible'
}

type ValidationCode = 'validationEmpty' | 'validationTooShort'

const validateImei = (imei: string): ValidationCode | null => {
  if (imei.length === 0) return 'validationEmpty'
  if (imei.length < 14) return 'validationTooShort'
  return null
}

const VALIDATION_ANALYTICS_KEY: Record<ValidationCode, string> = {
  validationEmpty: 'please_enter_your_imei_number',
  validationTooShort: 'imei_must_be_at_least_14_digits',
}

const getImeiI18n = (language: string): (typeof IMEI_CHECKER_I18N)['en'] =>
  IMEI_CHECKER_I18N[language as keyof typeof IMEI_CHECKER_I18N] ?? IMEI_CHECKER_I18N['en']

type ImeiImageProps = {
  xlImageSrc?: string
  mdImageSrc?: string
  xsImageSrc?: string
  imageAlt?: string
}

export type ImeiCheckerAllProps = ImeiImageProps & {
  checkerBannerHeading?: string
  checkerSubcopy?: string
  checkerTooltip?: string
  checkerCta?: string
  resultHeading?: string
  resultSuccessMessage?: string
  resultSuccessSubcopy?: Document
  resultNonCompatibleMessage?: string
  resultNonCompatibleSubcopy?: Document
  resultSplitSuccessMessage?: string
  resultSplitSuccessSubcopy?: Document
}

const ResponsiveCheckerImage = ({
  xlImageSrc = '',
  mdImageSrc = '',
  xsImageSrc = '',
  imageAlt = '',
}: ImeiImageProps) => (
  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <picture>
      {xlImageSrc && <source media={`(min-width: ${VIEWPORT.xl}px)`} srcSet={xlImageSrc} />}
      {mdImageSrc && <source media={`(min-width: ${VIEWPORT.md}px)`} srcSet={mdImageSrc} />}
      <img
        src={xsImageSrc || undefined}
        alt={imageAlt}
        style={{ width: '100%', maxWidth: 460, objectFit: 'contain' }}
      />
    </picture>
  </div>
)

type ImeiCheckerProps = ImeiImageProps & {
  checkerBannerHeading?: string
  checkerSubcopy?: string
  checkerTooltip?: string
  checkerCta?: string
  onResult: (result: DeviceResult) => void
}

const ImeiChecker = ({
  xlImageSrc,
  mdImageSrc,
  xsImageSrc,
  imageAlt = '',
  checkerBannerHeading = "Use your IMEI number to check your phone's compatibility",
  checkerSubcopy = 'Enter IMEI number',
  checkerTooltip = "Dial *#06# on your phone's dial pad to find your IMEI number. You can also go to Settings on your phone, it's usually under About.",
  checkerCta = 'Check compatibility',
  onResult,
}: ImeiCheckerProps) => {
  const {
    locale: { language },
  } = useContext(SiteBuilderContext)
  const i18n = getImeiI18n(language)

  const [imei, setImei] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [inputValidation, setInputValidation] = useState<'error' | 'success' | undefined>(undefined)
  const [inputFeedback, setInputFeedback] = useState<string | undefined>(undefined)
  const hasTrackedStart = useRef(false)

  const handleBlur = () => {
    if (!imei) return
    const validationCode = validateImei(imei)
    setInputValidation(validationCode ? 'error' : undefined)
    setInputFeedback(validationCode ? i18n[validationCode] : undefined)
  }

  const handleCheck = async () => {
    if (!hasTrackedStart.current) {
      hasTrackedStart.current = true
      alloyEvents.toolStart(window.location.href)
    }
    const digitsOnly = imei.replace(/\D/g, '')
    const validationCode = validateImei(digitsOnly)
    if (validationCode) {
      setInputValidation('error')
      setInputFeedback(i18n[validationCode])
      alloyEvents.validationError(window.location.href, VALIDATION_ANALYTICS_KEY[validationCode])
      return
    }
    setLoading(true)
    setError(null)

    try {
      const res = await fetch(`${API_BASE_PATH}api/imei`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imei: digitsOnly }),
      })

      const json = (await res.json()) as { ok: boolean; statusCode: number; data: DeviceResult | ApiError }

      if (!json.ok) {
        const errBody = json.data as ApiError
        if (json.statusCode === 404) {
          setError(i18n.deviceNotFound)
          alloyEvents.deviceNotFound(window.location.href, json.statusCode?.toString() ?? '')
        } else {
          setError(i18n.genericError)
          alloyEvents.genericError(
            window.location.href,
            'api_error',
            (errBody.message ?? 'api_error').toLowerCase().replace(/ /g, '_'),
            json.statusCode?.toString() ?? ''
          )
        }
      } else {
        onResult(json.data as DeviceResult)
      }
    } catch {
      setError(i18n.networkError)
      alloyEvents.genericError(window.location.href, 'network_error', 'network_error_please_try_again')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card
      variant={{ borderRadius: 'large', padding: 'compact' }}
      fullBleedContent={{
        position: { xs: 'top', md: 'left' },
        align: 'center',
        imgCol: { md: 6, lg: 6 },
        content: (
          <ResponsiveCheckerImage
            xlImageSrc={xlImageSrc}
            mdImageSrc={mdImageSrc}
            xsImageSrc={xsImageSrc}
            imageAlt={imageAlt}
          />
        ),
      }}
    >
      <StackView space={4}>
        <Typography block variant={{ size: 'h4' }} heading="h4">
          {checkerBannerHeading}
        </Typography>

        <StackView space={3}>
          <StackView space={2}>
            <StackView direction="row" space={1} tokens={{ alignItems: 'center' }}>
              <Typography>{checkerSubcopy}</Typography>
              <Tooltip inline content={checkerTooltip} />
            </StackView>
            <TextInput
              a11yLabel={checkerSubcopy}
              value={imei}
              type="number"
              autoComplete="off"
              onChange={(value: string) => {
                if (!hasTrackedStart.current) {
                  hasTrackedStart.current = true
                  alloyEvents.toolStart(window.location.href)
                }
                setImei((value ?? '').replace(/\D/g, ''))
                if (inputValidation === 'error') {
                  setInputValidation(undefined)
                  setInputFeedback(undefined)
                }
              }}
              onBlur={handleBlur}
              validation={inputValidation}
              feedback={inputFeedback}
              maxLength={15}
            />
          </StackView>
          <Button
            variant={{ priority: 'high' }}
            onPress={() => void handleCheck()}
            inactive={loading}
            heightFull={false}
          >
            {loading ? i18n.checking : checkerCta}
          </Button>
        </StackView>

        {error && (
          <Box variant={{ background: 'danger' }} space={3}>
            <Typography>{error}</Typography>
          </Box>
        )}
      </StackView>
    </Card>
  )
}

type ImeiCompatibilityResultProps = ImeiImageProps & {
  resultHeading?: string
  resultSuccessMessage?: string
  resultSuccessSubcopy?: Document
  checkAnotherDevice: string
  onReset: () => void
}

const ImeiCompatibilityResult = ({
  xlImageSrc,
  mdImageSrc,
  xsImageSrc,
  imageAlt = '',
  resultHeading = 'Compatibility results:',
  resultSuccessMessage = 'Your phone is VoLTE compatible',
  resultSuccessSubcopy,
  checkAnotherDevice,
  onReset,
}: ImeiCompatibilityResultProps) => {
  return (
    <Card
      variant={{ borderRadius: 'large', padding: 'compact' }}
      fullBleedContent={{
        position: { xs: 'top', md: 'left' },
        align: 'center',
        imgCol: { md: 6, lg: 6 },
        content: (
          <ResponsiveCheckerImage
            xlImageSrc={xlImageSrc}
            mdImageSrc={mdImageSrc}
            xsImageSrc={xsImageSrc}
            imageAlt={imageAlt}
          />
        ),
      }}
    >
      <StackView>
        <Typography block variant={{ size: 'h4' }} heading="h4">
          {resultHeading}
        </Typography>
        <Spacer space={3} />
        <StackView direction="row" space={2} tokens={{ alignItems: 'center' }}>
          <Icon icon={StatusSuccess as string} variant={{ color: 'success' }} />
          <Typography variant={{ bold: true }}>{resultSuccessMessage}</Typography>
        </StackView>
        <Spacer space={2} />
        {resultSuccessSubcopy && <RichText richText={resultSuccessSubcopy} paragraphSize="small" />}
        <Spacer space={3} />
        <TextButton
          icon={ArrowLeft as string}
          iconPosition="left"
          onPress={onReset}
          tokens={{ textLine: 'none', iconSize: 16 }}
        >
          {checkAnotherDevice}
        </TextButton>
      </StackView>
    </Card>
  )
}

type ImeiNotCompatibleResultProps = ImeiImageProps & {
  resultHeading?: string
  resultNonCompatibleMessage?: string
  resultNonCompatibleSubcopy?: Document
  checkAnotherDevice: string
  onReset: () => void
}

const ImeiNotCompatibleResult = ({
  xlImageSrc,
  mdImageSrc,
  xsImageSrc,
  imageAlt = '',
  resultHeading = 'Compatibility results:',
  resultNonCompatibleMessage = 'Your device will not be compatible',
  resultNonCompatibleSubcopy,
  checkAnotherDevice,
  onReset,
}: ImeiNotCompatibleResultProps) => {
  return (
    <Card
      variant={{ borderRadius: 'large', padding: 'compact' }}
      fullBleedContent={{
        position: { xs: 'top', md: 'left' },
        align: 'center',
        imgCol: { md: 6, lg: 6 },
        content: (
          <ResponsiveCheckerImage
            xlImageSrc={xlImageSrc}
            mdImageSrc={mdImageSrc}
            xsImageSrc={xsImageSrc}
            imageAlt={imageAlt}
          />
        ),
      }}
    >
      <StackView>
        <Typography block variant={{ size: 'h4' }} heading="h4">
          {resultHeading}
        </Typography>
        <Spacer space={3} />
        <StackView direction="row" space={2} tokens={{ alignItems: 'center' }}>
          <Icon icon={StatusError as string} variant={{ color: 'danger' }} />
          <Typography variant={{ bold: true }}>{resultNonCompatibleMessage}</Typography>
        </StackView>
        <Spacer space={2} />
        {resultNonCompatibleSubcopy && <RichText richText={resultNonCompatibleSubcopy} paragraphSize="small" />}
        <Spacer space={3} />
        <TextButton
          icon={ArrowLeft as string}
          iconPosition="left"
          onPress={onReset}
          tokens={{ textLine: 'none', iconSize: 16 }}
        >
          {checkAnotherDevice}
        </TextButton>
      </StackView>
    </Card>
  )
}

type ImeiPartialCompatibilityResultProps = ImeiImageProps & {
  resultHeading?: string
  resultSplitSuccessMessage?: string
  resultSplitSuccessSubcopy?: Document
  checkAnotherDevice: string
  onReset: () => void
}

const ImeiPartialCompatibilityResult = ({
  xlImageSrc,
  mdImageSrc,
  xsImageSrc,
  imageAlt = '',
  resultHeading = 'Compatibility results:',
  resultSplitSuccessMessage = 'Your phone is VoLTE compatible but does not support roaming',
  resultSplitSuccessSubcopy,
  checkAnotherDevice,
  onReset,
}: ImeiPartialCompatibilityResultProps) => {
  return (
    <Card
      variant={{ borderRadius: 'large', padding: 'compact' }}
      fullBleedContent={{
        position: { xs: 'top', md: 'left' },
        align: 'center',
        imgCol: { md: 6, lg: 6 },
        content: (
          <ResponsiveCheckerImage
            xlImageSrc={xlImageSrc}
            mdImageSrc={mdImageSrc}
            xsImageSrc={xsImageSrc}
            imageAlt={imageAlt}
          />
        ),
      }}
    >
      <StackView>
        <Typography block variant={{ size: 'h4' }} heading="h4">
          {resultHeading}
        </Typography>
        <Spacer space={3} />
        <StackView direction="row" space={2} tokens={{ alignItems: 'center' }}>
          <Icon icon={StatusWarning as string} variant={{ color: 'warning' }} />
          <Typography variant={{ bold: true }}>{resultSplitSuccessMessage}</Typography>
        </StackView>
        <Spacer space={2} />
        {resultSplitSuccessSubcopy && <RichText richText={resultSplitSuccessSubcopy} paragraphSize="small" />}
        <Spacer space={3} />
        <TextButton
          icon={ArrowLeft as string}
          iconPosition="left"
          onPress={onReset}
          tokens={{ textLine: 'none', iconSize: 16 }}
        >
          {checkAnotherDevice}
        </TextButton>
      </StackView>
    </Card>
  )
}

const ImeiCheckerWithComparison = ({
  xlImageSrc,
  mdImageSrc,
  xsImageSrc,
  imageAlt,
  checkerBannerHeading,
  checkerSubcopy,
  checkerTooltip,
  checkerCta,
  resultHeading,
  resultSuccessMessage,
  resultSuccessSubcopy,
  resultNonCompatibleMessage,
  resultNonCompatibleSubcopy,
  resultSplitSuccessMessage,
  resultSplitSuccessSubcopy,
}: ImeiCheckerAllProps) => {
  const {
    locale: { language },
  } = useContext(SiteBuilderContext)
  const i18n = getImeiI18n(language)

  const [activeResult, setActiveResult] = useState<DeviceResult | null>(null)

  const imageProps = { xlImageSrc, mdImageSrc, xsImageSrc, imageAlt }
  const resultCard = activeResult ? getResultCard(activeResult) : null
  const handleReset = () => setActiveResult(null)

  const handleResult = (result: DeviceResult) => {
    setActiveResult(result)
    const card = getResultCard(result)
    if (card === 'notCompatible') {
      alloyEvents.compatibilityNotCompatible(window.location.href)
    } else if (card === 'splitCompatible') {
      alloyEvents.compatibilitySuccessNoRoaming(window.location.href)
    } else {
      alloyEvents.compatibilitySuccess(window.location.href)
    }
  }

  return (
    <>
      {!activeResult && (
        <ImeiChecker
          {...imageProps}
          checkerBannerHeading={checkerBannerHeading}
          checkerSubcopy={checkerSubcopy}
          checkerTooltip={checkerTooltip}
          checkerCta={checkerCta}
          onResult={handleResult}
        />
      )}
      {resultCard === 'compatible' && (
        <ImeiCompatibilityResult
          {...imageProps}
          resultHeading={resultHeading}
          resultSuccessMessage={resultSuccessMessage}
          resultSuccessSubcopy={resultSuccessSubcopy}
          checkAnotherDevice={i18n.checkAnotherDevice}
          onReset={handleReset}
        />
      )}
      {resultCard === 'splitCompatible' && (
        <ImeiPartialCompatibilityResult
          {...imageProps}
          resultHeading={resultHeading}
          resultSplitSuccessMessage={resultSplitSuccessMessage}
          resultSplitSuccessSubcopy={resultSplitSuccessSubcopy}
          checkAnotherDevice={i18n.checkAnotherDevice}
          onReset={handleReset}
        />
      )}
      {resultCard === 'notCompatible' && (
        <ImeiNotCompatibleResult
          {...imageProps}
          resultHeading={resultHeading}
          resultNonCompatibleMessage={resultNonCompatibleMessage}
          resultNonCompatibleSubcopy={resultNonCompatibleSubcopy}
          checkAnotherDevice={i18n.checkAnotherDevice}
          onReset={handleReset}
        />
      )}
    </>
  )
}

export default ImeiCheckerWithComparison
